import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, CheckCircle2, Loader2, Search, ShieldCheck, Trash2, XCircle } from "lucide-react";
import { toE164Loose, type PrivacyEraseResult, type PrivacyLookup } from "@wedding/shared";
import { adminPrivacyApi } from "../lib/api";
import { AppLink } from "../lib/route-primitives";
import { paths } from "../lib/paths";
import { cn } from "../lib/utils";

/**
 * KVKK başvuruları (F9) — bir kişi verisinin silinmesini istediğinde:
 * telefonla TÜM düğünlerde bul → önizle → sil/anonimleştir.
 * Worker: `/api/admin/privacy` (lib/privacy-erase.ts). Arama da audit'lenir.
 */

function Toast({ msg, ok, onDone }: { msg: string; ok: boolean; onDone: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onDone, 3200);
    return () => clearTimeout(timer);
  }, [onDone]);
  return (
    <div role="status" aria-live="polite" className={cn(
      "fixed bottom-6 left-1/2 -translate-x-1/2 z-[200]",
      "flex items-center gap-2.5 px-4 py-3 rounded-2xl shadow-xl text-sm font-medium",
      ok ? "bg-emerald-500 text-white" : "bg-red-500 text-white",
    )}>
      {ok ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <XCircle className="h-4 w-4 shrink-0" />}
      {msg}
    </div>
  );
}

export function PrivacyPage() {
  const { t } = useTranslation();
  const [input, setInput] = useState("");
  const [phone, setPhone] = useState<string | null>(null);
  const [lookup, setLookup] = useState<PrivacyLookup | null>(null);
  const [erased, setErased] = useState<PrivacyEraseResult | null>(null);
  const [busy, setBusy] = useState<"lookup" | "erase" | null>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const clearToast = useCallback(() => setToast(null), []);

  const normalized = toE164Loose(input);
  const errMsg = (err: unknown) => (err instanceof Error ? err.message : String(err));

  const search = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!normalized) {
      setToast({ msg: t("privacyAdmin.invalidPhone"), ok: false });
      return;
    }
    setBusy("lookup");
    setErased(null);
    try {
      const res = await adminPrivacyApi.lookup(normalized);
      setLookup(res.data ?? null);
      setPhone(normalized);
    } catch (err) {
      setToast({ msg: t("privacyAdmin.lookupFailed", { error: errMsg(err) }), ok: false });
    } finally {
      setBusy(null);
    }
  };

  const erase = async () => {
    if (!phone || !lookup) return;
    if (!window.confirm(t("privacyAdmin.eraseConfirm", { phone: lookup.phoneMasked, count: lookup.guests.length }))) return;
    setBusy("erase");
    try {
      const res = await adminPrivacyApi.erase(phone);
      setErased(res.data ?? null);
      const again = await adminPrivacyApi.lookup(phone);
      setLookup(again.data ?? null);
      setToast({ msg: t("privacyAdmin.erased"), ok: true });
    } catch (err) {
      setToast({ msg: t("privacyAdmin.eraseFailed", { error: errMsg(err) }), ok: false });
    } finally {
      setBusy(null);
    }
  };

  const nothing =
    lookup !== null &&
    lookup.guests.length === 0 &&
    lookup.authCodes === 0 &&
    !lookup.contactRequests &&
    lookup.auditRows === 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-serif font-bold text-foreground flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-primary" aria-hidden="true" />
          {t("privacyAdmin.title")}
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{t("privacyAdmin.description")}</p>
      </div>

      <form onSubmit={(e) => void search(e)} className="rounded-2xl border border-border bg-card px-5 py-4 space-y-2">
        <label htmlFor="privacy-phone" className="text-xs font-medium text-foreground">
          {t("privacyAdmin.phoneLabel")}
        </label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            id="privacy-phone"
            type="tel"
            inputMode="tel"
            autoComplete="off"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="0555 123 45 67"
            aria-invalid={(input.length > 0 && !normalized) || undefined}
            className="w-full sm:max-w-xs rounded-xl border border-border bg-background px-3 py-2.5 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/20 aria-[invalid=true]:border-red-500"
          />
          <button
            type="submit"
            disabled={busy !== null || !normalized}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
          >
            {busy === "lookup" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            {t("privacyAdmin.search")}
          </button>
        </div>
        <p className="text-xs text-muted-foreground" data-testid="privacy-normalized">
          {normalized ? t("privacyAdmin.willSearch", { phone: normalized }) : t("privacyAdmin.phoneHint")}
        </p>
      </form>

      {erased && (
        <div data-testid="privacy-erased" className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 px-5 py-4 text-sm text-foreground">
          <p className="font-medium flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden="true" />
            {t("privacyAdmin.erasedSummary", {
              phone: erased.phoneMasked,
              guests: erased.guests,
              media: erased.media,
              audit: erased.auditRowsMasked,
            })}
          </p>
        </div>
      )}

      {lookup && (
        <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="privacy-result">
          <div className="px-5 py-4 border-b border-border bg-muted/30">
            <h2 className="text-sm font-semibold text-foreground">
              {t("privacyAdmin.resultTitle", { phone: lookup.phoneMasked })}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t("privacyAdmin.resultMeta", {
                authCodes: lookup.authCodes,
                leads: lookup.contactRequests ?? "—",
                audit: lookup.auditRows,
              })}
            </p>
            {(lookup.orders ?? 0) > 0 && (
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-1" data-testid="privacy-orders">
                {t("privacyAdmin.ordersKept", { count: lookup.orders ?? 0 })}
              </p>
            )}
          </div>

          <div className="px-5 py-4 space-y-4">
            {lookup.organizers > 0 && (
              <p className="flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
                <span>
                  {t("privacyAdmin.organizerNote")}{" "}
                  <AppLink to={paths.users} className="underline underline-offset-2 cursor-pointer">
                    {t("nav.users")}
                  </AppLink>
                </span>
              </p>
            )}

            {nothing ? (
              <p className="text-sm text-muted-foreground" data-testid="privacy-empty">{t("privacyAdmin.nothing")}</p>
            ) : lookup.guests.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("privacyAdmin.noGuests")}</p>
            ) : (
              <ul className="divide-y divide-border rounded-xl border border-border">
                {lookup.guests.map((g) => (
                  <li key={g.id} className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {g.name ?? t("privacyAdmin.noName")}
                        <span className="ml-2 text-xs font-normal text-muted-foreground">
                          {g.weddingTitle ?? g.weddingSlug} · /{g.weddingSlug}
                        </span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {g.isDeleted ? t("privacyAdmin.guestRemoved") : t("privacyAdmin.guestActive")}
                      </p>
                    </div>
                    <p className="text-xs tabular-nums text-muted-foreground">
                      {t("privacyAdmin.counts", { media: g.media, comments: g.comments, likes: g.likes, rsvp: g.rsvp, sms: g.smsSends })}
                    </p>
                  </li>
                ))}
              </ul>
            )}

            <div className="rounded-xl border border-red-500/30 bg-red-500/5 px-4 py-3 space-y-3">
              <p className="text-sm text-foreground">{t("privacyAdmin.eraseExplain")}</p>
              <button
                type="button"
                onClick={() => void erase()}
                disabled={busy !== null || nothing}
                className="inline-flex items-center gap-2 rounded-full bg-red-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                {busy === "erase" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                {t("privacyAdmin.erase")}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast msg={toast.msg} ok={toast.ok} onDone={clearToast} />}
    </div>
  );
}
